import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, catchError, map, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { DashboardService } from '../../../services/dashboard/dashboard.service';
import { WidgetAdapterService } from '../../../services/dashboard/widget-adapter.service';
import { WidgetData } from './duna-dashboard.component';
import { DashboardConfigDTO, DashboardWidgetConfigDTO } from '../../../dto/dashboard/dashboard.dto';

// Define interface to fix TypeScript errors
export interface DunaDashboardAdapterInterface {
  getWidgets(): Observable<any[]>;
  saveWidgets(widgets: any[]): Observable<boolean>;
}

/**
 * This adapter connects the duna-dashboard component to the dashboard service
 * using the simplified service implementation
 */
@Injectable({
  providedIn: 'root'
})
export class DunaDashboardAdapter implements DunaDashboardAdapterInterface {
  private dashboardService = inject(DashboardService);
  private widgetAdapter = inject(WidgetAdapterService);
  private savedWidgets$ = new BehaviorSubject<any[]>([]);
  // Add a flag to track if widgets have been created
  private widgetsCreated = false;
  // Cache of widget IDs to prevent duplicates
  private widgetIdCache = new Set<string>();
  // Cache of allowed widgets for current user role
  private allowedWidgets: string[] = [];
  private allowedWidgetsRequest$: Observable<string[]> | null = null;

  constructor() {}

  /**
   * Get dashboard widgets for the duna dashboard component
   */
  getWidgets(): Observable<any[]> {
    
    // Always fetch from backend - don't use cached widgets to ensure we get the latest from template
    // The component will handle caching and display
    // Allowed-widget permissions are loaded here, not in the constructor, so a public
    // share link never calls the authenticated /dashboard/allowed-widgets endpoint.
    return this.ensureAllowedWidgets().pipe(
      switchMap(() => this.dashboardService.getCurrentDashboardConfig().pipe(
      tap(dashboard => {
        
        // Log the widget sizes from the dashboard config
        if (dashboard?.widgets) {
          dashboard.widgets.forEach(widget => {
          });
        }
      }),
      map(dashboard => {
        // Always process widgets from backend - don't use cache
        if (!dashboard || !dashboard.widgets || dashboard.widgets.length === 0) {
          console.warn('[DunaDashboardAdapter] No dashboard config or widgets found from backend - returning empty array');
          console.warn('[DunaDashboardAdapter] Will NOT create default widgets - only loading from backend');
          
          // Return empty array - do NOT create default widgets
          // The component should handle empty state or retry loading
          this.savedWidgets$.next([]);
          this.widgetsCreated = false;
          
          return [];
        }
        
        // Convert dashboard config to widget array using adapter
        let widgets = this.widgetAdapter.convertDashboardConfigDTOToWidgetDataArray(dashboard);
        
        // Filter widgets based on user permissions
        widgets = widgets.filter(widget => {
          const isAllowed = this.isWidgetAllowed(widget.type);
          if (!isAllowed) {
          }
          return isAllowed;
        });
        
        
        // Fix for widgets with undefined types and sizes
        if (dashboard?.widgets?.length > 0) {
          
          // Log each widget's properties
          dashboard.widgets.forEach((w, i) => {
          });
          
          // If we have widgets with undefined types, we need to recreate them properly
          const hasUndefinedTypes = dashboard.widgets.some(w => !w.widgetType);
          
          if (hasUndefinedTypes) {
            
            // Create new widgets based on the positions from the dashboard config
            widgets = dashboard.widgets
              .filter(widget => this.isWidgetAllowed(widget.widgetType || 'userProgress'))
              .map((widget, index) => {
                // Get position information from the broken widget
                const position = widget.position || {};
                const x = position.x ?? 0;
                const y = position.y ?? 0;
                
                // Default to userProgress type since that's what we're working with
                const type = widget.widgetType || 'userProgress';
                
                // Create widget ID using sequence number
                const timestamp = Date.now();
                const id = widget.id || `widget-${type}-${timestamp}-${index}`;
                
                // Create proper title based on index
                let title = 'Bruger fremgang - Oversigt';
                
                // Get size from position object (backend sends cols/rows in position)
                const cols = widget.position?.cols || 6;
                const rows = widget.position?.rows || 4;
                
                // Create a fully formed widget
                return {
                  id: id,
                  type: type,
                  title: title,
                  position: {
                    x: x,
                    y: y,
                    cols: cols,
                    rows: rows,
                    id: id
                  },
                  x: x,
                  y: y,
                  cols: cols,
                  rows: rows,
                  dataSource: 'user_progress',
                  settings: {
                    category: type,
                    filters: { group: 'all' },
                    fields: ['name', 'path', 'percentComplete', 'station'],
                    displayType: 'table'
                  }
                };
              });
          }
        }
        
        // Force proper Danish titles and sizes for specific widget types
        widgets.forEach(widget => {
          // Get size from position if available, otherwise use defaults
          const cols = widget.position?.cols || widget.cols || 6;
          const rows = widget.position?.rows || widget.rows || 4;
          
          // Update widget properties with correct sizes
          widget.cols = cols;
          widget.rows = rows;
          if (widget.position) {
            widget.position.cols = cols;
            widget.position.rows = rows;
          }
          
          // Handle userProgress widgets specifically
          if (widget.type === 'userProgress' || !widget.type) {
            // Make sure type is set
            widget.type = 'userProgress';
            
            // Check for English titles and replace with Danish
            if (widget.title === 'User Progress') {
              widget.title = 'Bruger fremgang - Oversigt';
            } else if (widget.title === 'Educational Paths') {
              widget.title = 'Uddannelsesveje';
            }
          }
        });
        
        // Log the widget sizes after conversion and adjustments
        widgets.forEach(widget => {
          
          // Add to ID cache
          this.widgetIdCache.add(widget.id);
        });
        
        // Save widgets for future reference
        this.savedWidgets$.next(widgets);
        // Mark as created
        this.widgetsCreated = true;
        
        return widgets;
      }),
      catchError(err => {
        console.error('[DunaDashboardAdapter] Error getting widgets from backend:', err);
        console.warn('[DunaDashboardAdapter] Will NOT create default widgets - only loading from backend');
        
        // Return empty array - do NOT create default widgets or use cache
        // The component should retry loading from backend
        this.savedWidgets$.next([]);
        this.widgetsCreated = false;
        
        return of([]);
      })
    )));
  }

  /**
   * Create default widgets with proper Danish titles and sizes
   * Only creates widgets that are allowed for the current user role
   */
  private createDefaultWidgets(): any[] {
    const timestamp = Date.now();
    const standardFilters = { 
      group: 'all',
      sortBy: 'name',
      sortDirection: 'asc'
    };
    
    const defaultWidgets = [
      {
        id: `widget-userProgress-${timestamp}`,
        type: 'userProgress',
        title: 'Bruger fremgang - Oversigt',
        position: {
          x: 0,
          y: 0,
          cols: 6,
          rows: 4,
          id: `widget-userProgress-${timestamp}`
        },
        x: 0,
        y: 0,
        cols: 6,
        rows: 4,
        dataSource: 'user_progress',
        defaultFilters: standardFilters,
        settings: {
          category: 'userProgress',
          filters: standardFilters,
          fields: ['name', 'path', 'percentComplete', 'station'],
          displayType: 'table'
        }
      }
    ];
    
    // Filter to only include allowed widgets
    return defaultWidgets.filter(widget => this.isWidgetAllowed(widget.type));
  }

  /**
   * Save the entire dashboard
   * @param widgets The widgets to save
   * @param forceApiSave Whether to force saving to API (defaults to false)
   */
  saveWidgets(widgets: any[], forceApiSave: boolean = false): Observable<boolean> {
    
    // Log widget sizes being saved
    widgets.forEach(widget => {
    });
    
    // Store in behavior subject for caching
    this.savedWidgets$.next([...widgets]);
    
    // Save to localStorage as backup
    localStorage.setItem('dunaWidgets', JSON.stringify(widgets));
    
    // Get current dashboard config
    return this.dashboardService.getCurrentDashboardConfig().pipe(
      take(1),
      map(currentDashboard => {
        // Create a new dashboard config if none exists
        if (!currentDashboard) {
          return {
            id: '1',
            name: 'Default Dashboard',
            widgets: widgets.map(widget => ({
              id: widget.id,
              widgetType: widget.type || 'userProgress',
              title: widget.title,
              position: {
                x: widget.x || widget.position?.x || 0,
                y: widget.y || widget.position?.y || 0,
                cols: widget.cols || widget.position?.cols || 6,
                rows: widget.rows || widget.position?.rows || 4
              },
              dataSource: widget.dataSource || 'user_progress',
              defaultFilters: widget.settings?.filters || {}
            }))
          };
        }
        
        // Update existing dashboard config
        return {
          ...currentDashboard,
          widgets: widgets.map(widget => ({
            id: widget.id,
            widgetType: widget.type || 'userProgress',
            title: widget.title,
            position: {
              x: widget.x || widget.position?.x || 0,
              y: widget.y || widget.position?.y || 0,
              cols: widget.cols || widget.position?.cols || 6,
              rows: widget.rows || widget.position?.rows || 4
            },
            dataSource: widget.dataSource || 'user_progress',
            defaultFilters: widget.settings?.filters || {}
          }))
        };
      }),
      map(config => {
        try {
          // Use saveCurrentDashboardConfig if available, otherwise just update the BehaviorSubject
          if (this.dashboardService['saveCurrentDashboardConfig']) {
            this.dashboardService['saveCurrentDashboardConfig'](config, forceApiSave).subscribe();
          } else {
            this.dashboardService['currentDashboard$'].next(config);
            
            // Also save to cache for offline use
            this.dashboardService['saveToCache'](config);
          }
          
          return true;
        } catch (error) {
          console.error('[DunaDashboardAdapter] Error saving widgets:', error);
          return false;
        }
      }),
      catchError(error => {
        console.error('[DunaDashboardAdapter] Error in saveWidgets:', error);
        return of(false);
      })
    );
  }

  /**
   * Add a new widget to the dashboard
   */
  addWidget(widget: WidgetData): Observable<WidgetData> {
    const widgetConfig = this.convertToWidgetConfig(widget);
    
    // Use the addWidget method from dashboardService
    this.dashboardService.addWidget(widgetConfig).subscribe();
    
    // Return the original widget for the UI
    return of(widget);
  }

  /**
   * Update a widget
   */
  updateWidget(widgetId: string, widget: Partial<WidgetData>): Observable<WidgetData> {
    const updates = this.convertToWidgetConfig(widget as WidgetData);
    
    // Use updateWidget from dashboardService
    this.dashboardService.updateWidget(widgetId, updates).subscribe();
    
    // Return updated widget for UI
    return of({...widget, id: widgetId} as WidgetData);
  }

  /**
   * Remove a widget
   */
  removeWidget(widgetId: string): Observable<void> {
    // Call removeWidget and convert the return value to void
    return this.dashboardService.removeWidget(widgetId).pipe(
      map(() => undefined)
    );
  }

  /**
   * Update widget positions
   */
  updateWidgetPositions(positions: { id: string, position: any }[]): Observable<WidgetData[]> {
    // Get current widgets
    return this.getWidgets().pipe(
      map(widgets => {
        // Update positions in widgets
        const updatedWidgets = widgets.map(widget => {
          const posUpdate = positions.find(p => p.id === widget.id);
          if (posUpdate) {
            return {
              ...widget,
              position: posUpdate.position,
              x: posUpdate.position.x,
              y: posUpdate.position.y,
              cols: posUpdate.position.cols,
              rows: posUpdate.position.rows
            };
          }
          return widget;
        });
        
        // Save updates
        this.saveWidgets(updatedWidgets).subscribe();
        
        return updatedWidgets;
      })
    );
  }

  /**
   * Helper method to convert DashboardWidgetConfigDTO to WidgetData
   */
  private convertToWidgetData(config: DashboardWidgetConfigDTO): WidgetData {
    // Get default size from widget adapter if size is missing
    const defaultSize = this.widgetAdapter.getWidgetConfig(config.widgetType)?.defaultSize || { cols: 2, rows: 2 };
    
    // Extract size safely, using defaults if properties are missing
    const cols = config.position?.cols || defaultSize.cols;
    const rows = config.position?.rows || defaultSize.rows;
    
    return {
      id: config.id,
      type: config.widgetType,
      title: config.title || config.widgetType,
      position: {
        x: config.position?.x || 0,
        y: config.position?.y || 0,
        cols: cols,
        rows: rows,
        id: config.id
      },
      x: config.position?.x || 0,
      y: config.position?.y || 0,
      cols: cols,
      rows: rows,
      dataSource: config.dataSource,
      settings: {
        category: config.widgetType,
        filters: config.defaultFilters || {},
        fields: this.widgetAdapter.getDefaultFields(config.widgetType),
        displayType: this.widgetAdapter.getDefaultDisplayType(config.widgetType)
      }
    };
  }

  /**
   * Load allowed widgets for the current user role the first time a signed-in dashboard is read.
   */
  private ensureAllowedWidgets(): Observable<string[]> {
    if (!this.allowedWidgetsRequest$) {
      this.allowedWidgetsRequest$ = this.dashboardService.getAllowedWidgets().pipe(
        tap((widgets) => {
          this.allowedWidgets = widgets;
        }),
        catchError((error) => {
          console.error('[DunaDashboardAdapter] Error loading allowed widgets:', error);
          this.allowedWidgets = ['userProgress', 'userProgressStats', 'locationProgress', 'simpleNumber', 'cateringSummary', 'eventsCalendar', 'statusWidget', 'yearComparison', 'educationOverview', 'quickLinks', 'reminders', 'upcomingExercises', 'userOverview', 'locationsOverview'];
          return of(this.allowedWidgets);
        }),
        shareReplay(1)
      );
    }
    return this.allowedWidgetsRequest$;
  }

  /**
   * Check if a widget type is allowed for current user role
   */
  private isWidgetAllowed(widgetType: string): boolean {
    return this.allowedWidgets.includes(widgetType);
  }

  /**
   * Helper method to convert WidgetData to DashboardWidgetConfigDTO
   */
  private convertToWidgetConfig(widget: WidgetData): DashboardWidgetConfigDTO {
    
    // Make sure we have a valid type
    const type = widget.type || 'userProgress';
    
    return {
      id: widget.id,
      widgetType: type,
      title: widget.title || `Widget ${widget.id}`,
      description: '',
      position: {
        x: widget.x || widget.position?.x || 0,
        y: widget.y || widget.position?.y || 0,
        cols: widget.cols || widget.position?.cols || 6,
        rows: widget.rows || widget.position?.rows || 4
      },
      dataSource: widget.dataSource || type,
      defaultFilters: widget.settings?.filters || {}
    };
  }
} 